/**
 * Extends and overwrites next.js app file.
 * Good for handling something globally.
 */
import 'styles/_import.scss';

import { CacheProvider, EmotionCache } from '@emotion/react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Footer from 'components/Footer/Footer';
import GlobalLoader from 'components/GlobalLoader/GlobalLoader';
import GlobalScripts from 'components/GlobalScripts/GlobalScripts';
import LoginModal from 'components/LoginModal/LoginModal';
import MainContainer from 'components/MainContainer/MainContainer';
import PollMessages from 'components/PollMessages/PollMessages';
import SideMenu from 'components/Sidemenu/Sidemenu';
import SnackBarProvider from 'components/SnackBarProvider/SnackBarProvider';
import TabTitle from 'components/TabTitle/TabTitle';
import config, { Language, Locale } from 'config';
import GlobalContextProvider from 'contexts/globalContext';
import jsCookie from 'js-cookie';
import { NextPageContext } from 'next';
import { AppProps } from 'next/app';
import Head from 'next/head';
import Router from 'next/router';
import { BreadcrumbJsonLd, DefaultSeo } from 'next-seo';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Provider } from 'react-redux';
import { callAppDataInit } from 'store/actions/common.actions';
import { setGlobalLoader } from 'store/actions/global-loader.actions';
import { setYagaUUID } from 'store/actions/user.actions';
import defaultTheme from 'themes/default';
import { GTM, PIXEL } from 'utils/analytics';
import { checkLogin } from 'utils/authentication';
import { Braze } from 'utils/braze';
import { createLtrEmotionCache, createRtlEmotionCache } from 'utils/createEmotionCache';
import i18n, { initI18n, pollForTranslationUpdates, STORAGE_LOCALE_KEY } from 'utils/i18n';
import useScrollRestoration from 'utils/keep-scroll';
import { generateRandomString } from 'utils/random';
import useRouteHistoryTracker from 'utils/route-history-tracker';
import { getJsonLdBreadcrumbsList } from 'utils/seo-data';
import { getCookies } from 'utils/server-cookies';

import { DownloadAppBanner } from '../components/DownloadAppBanner/DownloadAppBanner';
import GrowthBookWrapper from '../components/GrowthBookWrapper/GrowthBookWrapper';
import { Onboarding } from '../components/Onboarding/Onboarding';
import { PrefetchClientSide } from '../components/PrefetchClientSide/PrefetchClientSide';
import { SeoMetaTags } from '../components/SeoMetaTags/SeoMetaTags';
import { TermsUpdateModal } from '../components/TermsUpdateModal/TermsUpdateModal';
import { UserPhoneVerificationModal } from '../components/UserPhoneVerificationModal/UserPhoneVerificationModal';
import { wrapper } from '../store/index';
import { prefetchToStore } from '../store/prefetchToStore';

let didPop = false;
const facebookDomainVerification = config.get('facebook.domainVerification');

const clientSideIsRtl = typeof window !== 'undefined' && localStorage.getItem('rtl-mode:enabled') === 'true';
const clientSideEmotionCache = clientSideIsRtl ? createRtlEmotionCache() : createLtrEmotionCache();
const activeTheme = clientSideIsRtl ? createTheme({ ...defaultTheme, direction: 'rtl' } as any) : defaultTheme;

const YAGA_ANONYMOUS_UUID_COOKIE = 'yaga_anonymous_uuid';

const MyApp = ({
  Component,
  emotionCache = clientSideEmotionCache,
  ...rest
}: AppProps & { locale: Locale; yagaUUID: string; err: any; jsonLdBreadcrumbs: any[]; emotionCache: EmotionCache }) => {
  const { store, props } = wrapper.useWrappedStore(rest);
  const { pageProps, router, locale, err, jsonLdBreadcrumbs } = props;

  const country = config.get('country');

  // Get user data from store for dynamic language initialization
  const userData = store.getState()?.userData?.user;

  let yagaUUID = jsCookie.get(YAGA_ANONYMOUS_UUID_COOKIE);

  const pollForTranslationUpdatesRef = useRef<NodeJS.Timeout>(undefined);

  if (!yagaUUID) {
    yagaUUID = `yaga_${generateRandomString()}`;
    jsCookie.set(YAGA_ANONYMOUS_UUID_COOKIE, yagaUUID, { expires: 30 });
  }

  // Store yagaUUID to store
  setYagaUUID(yagaUUID);

  const { asPath, route } = router;
  const dataStore = store.getState();

  const [popStateData, setPopStateData] = useState(null);

  const defaultPageImage = config.get(`facebook.defaultImage[${country}]`);

  useScrollRestoration(router);
  useRouteHistoryTracker(router);

  // Init default language once on render both server and client side
  // Use user's country if available for dynamic language availability
  useMemo(() => {
    const userCountry = userData?.country;

    initI18n(locale, userCountry);
  }, [locale, userData?.country]);

  // Re-initialize i18n when user country changes (e.g., after login or profile update)
  useEffect(() => {
    const currentUser = store.getState()?.userData?.user;

    if (currentUser?.country && currentUser.country !== userData?.country) {
      initI18n(locale, currentUser.country);
    }
  }, [store.getState()?.userData?.user?.country, locale]);

  useEffect(() => {
    clearInterval(pollForTranslationUpdatesRef.current);

    pollForTranslationUpdatesRef.current = pollForTranslationUpdates();

    return () => {
      clearInterval(pollForTranslationUpdatesRef.current);
    };
  }, []);

  const [isRouteLoading, setRouteLoading] = useState(false);
  const [breadcrumbsList, setBreadcrumbsList] = useState(jsonLdBreadcrumbs);
  const [prevLocale, setPrevLocale] = useState(locale);

  useEffect(() => {
    document.documentElement.dir = clientSideIsRtl ? 'rtl' : 'ltr';
  }, []);

  useEffect(() => {
    callAppDataInit();

    const breadcrumbs = getJsonLdBreadcrumbsList(router);

    setBreadcrumbsList(breadcrumbs);
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side');

    if (jssStyles) {
      try {
        jssStyles.parentElement.removeChild(jssStyles);
      } catch (_) {
        // Do nothing
      }
    }

    // If back/forward button was clicked, trigger custom event
    Router.beforePopState(() => {
      setPopStateData(Router);
      const beforePopStateEvent = new CustomEvent('beforepopstate', {
        detail: Router,
      });

      window.dispatchEvent(beforePopStateEvent);
      didPop = true;

      return true;
    });

    const trackPageViews = (url) => {
      // Fire Analytics
      PIXEL.logPageView();
      GTM.pageview(url);
    };

    // Detect loading states by listening to route events
    const handleStart = (url) => {
      window.route = url;

      if (url !== Router.asPath) {
        setRouteLoading(true);

        // If did not pop, remove pop state data
        if (!didPop) {
          setPopStateData(null);
        }
        // Mark as did not pop
        didPop = false;
      }
    };

    let lastTrackedUrl = null;

    const handleComplete = (url) => {
      if (window.location.pathname + '' + window.location.search === url && lastTrackedUrl !== url) {
        trackPageViews(url);
        lastTrackedUrl = url;
      }

      const refreshedBreadcrumbs = getJsonLdBreadcrumbsList(router);

      setBreadcrumbsList(refreshedBreadcrumbs);

      setRouteLoading(false);
      setGlobalLoader(false);
    };

    const handleError = () => {
      setRouteLoading(false);
      setGlobalLoader(false);
    };

    Router.events.on('routeChangeStart', handleStart);
    Router.events.on('routeChangeComplete', handleComplete);
    Router.events.on('routeChangeError', handleError);

    // Facebook pixel tracking
    PIXEL.init();

    // Initialize Braze SDK for marketing automation
    Braze.initialize();

    // Check login and init guest if not logged in
    checkLogin();
  }, []);

  // Detect language changes and refetch data when language changes
  useEffect(() => {
    // Skip the initial call since getInitialProps already handled it
    if (locale !== prevLocale) {
      prefetchToStore(store);
      setPrevLocale(locale);
    }
  }, [locale, prevLocale]);

  return (
    <>
      {/* initial head */}
      <Head>
        {/* Disable google translate suggest */}
        <meta name="google" content="notranslate" />

        {/* Responsive viewport meta tag - to ensure proper rendering and touch zooming for all devices */}
        <meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width" />

        {/* Theme color for mobile browser */}
        <meta name="theme-color" content="#ff7d6b" />

        {/* Facebook domain verification for Facebook PIXEL */}
        {facebookDomainVerification && <meta name="facebook-domain-verification" content={facebookDomainVerification} />}

        {/* Sentry page load tracing */}
        <meta name="sentry-trace" content="{{ span.toTraceparent() }}" />
      </Head>

      <Provider store={store}>
        <GlobalScripts />
        <GlobalContextProvider>
          <CacheProvider value={emotionCache}>
            <GrowthBookWrapper>
              <ThemeProvider theme={activeTheme}>
                {/* Tab titles */}
                <TabTitle folderPath={route} />

                <Typography component="div" className="global-wrapper">
                  {/* website structure */}
                  <MainContainer folderPath={route} browserNavigationButtonPressed={!!popStateData}>
                    <SnackBarProvider>
                      <SideMenu />

                      <DefaultSeo
                        title={i18n.t('meta.title')}
                        description={i18n.t('meta.description')}
                        defaultOpenGraphImageHeight={1200}
                        defaultOpenGraphImageWidth={630}
                        openGraph={{
                          title: i18n.t('meta.title'),
                          description: i18n.t('meta.description'),
                          type: 'website',
                          url: typeof window !== 'undefined' && window.location.href,
                          site_name: i18n.t('meta.title'),
                          images: [
                            {
                              url: defaultPageImage,
                              width: 1200,
                              height: 630,
                              alt: i18n.t('meta.title'),
                            },
                          ],
                        }}
                        facebook={{
                          appId: config.get('facebook.appId'),
                        }}
                      />

                      <SeoMetaTags />

                      {breadcrumbsList?.length > 0 && <BreadcrumbJsonLd itemListElements={breadcrumbsList} />}

                      <Component
                        store={dataStore}
                        isLoading={isRouteLoading}
                        route={route}
                        asPath={asPath}
                        popStateData={popStateData}
                        browserNavigationButtonPressed={!!popStateData}
                        err={err}
                        {...pageProps}
                      />

                      <Onboarding />
                      <TermsUpdateModal />
                      <LoginModal />
                      <UserPhoneVerificationModal />
                      <Footer />
                      <DownloadAppBanner />
                      <PollMessages />
                      <PrefetchClientSide />
                      <GlobalLoader />
                    </SnackBarProvider>
                  </MainContainer>
                </Typography>
              </ThemeProvider>
            </GrowthBookWrapper>
          </CacheProvider>
        </GlobalContextProvider>
      </Provider>
    </>
  );
};

const getInitialLocale = (ctx: NextPageContext) => {
  const cookies = getCookies(ctx.req);

  const locale = typeof window !== 'undefined' ? jsCookie.get(STORAGE_LOCALE_KEY) : cookies[STORAGE_LOCALE_KEY];
  const countryLanguage = config.get('countryLanguage');
  const country = config.get('country');
  const defaultLocale = `${countryLanguage}-${country}`;

  // If user has a locale preference, validate it's a real language before using it
  if (locale) {
    const userLanguage = locale.split('-')[0] as Language;
    const isValidLanguage = Object.values(Language).includes(userLanguage);

    if (isValidLanguage) {
      // Allow any valid user language regardless of deployment country restrictions
      return locale;
    }
  }

  return defaultLocale;
};

/**
 * NB! The client-side store data is NOT available on the server-side at the moment
 * of the getInitialPageProps and getServerSideProps requests,
 * so we should NEVER overwrite data directly on the server side on the Hydrate action.
 * Instead we should handle data flows individually.
 * https://github.com/kirill-konshin/next-redux-wrapper#server-and-client-state-separation
 */
MyApp.getInitialProps = wrapper.getInitialPageProps((store) =>
  // @ts-ignore unknown props.
  async ({ ctx, err, router }: NextPageContext) => {
    const jsonLdBreadcrumbs = getJsonLdBreadcrumbsList(router);
    const initLocale = getInitialLocale(ctx);

    // Set locale on SSR store (not global store) so prefetchToStore can read it
    store.dispatch({ type: 'LOCALE_SET', locale: initLocale });

    // Only prefetch on server-side (initial page load), not on client-side navigation
    if (ctx.req) {
      await prefetchToStore(store);
    }

    return {
      locale: initLocale,
      jsonLdBreadcrumbs,
      err,
    };
  },
);

export default MyApp;
